import type { InitOptions } from 'i18next';
import { datadogRum } from '@datadog/browser-rum';

import { loadAppLocaleResource } from './loadLocaleResource';

type MissingKeyHandler = Exclude<NonNullable<InitOptions['missingKeyHandler']>, false>;
type MissingKeyArguments = [
  lngs: readonly string[],
  ns: string,
  key: string,
  fallbackValue: string,
  updateMissing: boolean,
  options: unknown,
];
type CrossCheckResult = 'confirmed_missing' | 'cross_check_unavailable' | 'skipped';

type MissingKeyReport = {
  lngs: readonly string[];
  ns: string;
  key: string;
  fallbackValue: string;
  updateMissing: boolean;
  options: unknown;
  crossCheckResult: CrossCheckResult;
};

const reportMissingKey = ({ lngs, ns, key, fallbackValue, updateMissing, options, crossCheckResult }: MissingKeyReport) => {
  datadogRum.addError(new Error(`Missing key: ${key} namespace: ${ns} lngs: ${lngs.join(',')}`), {
    lngs,
    key,
    ns,
    options,
    fallbackValue,
    updateMissing,
    crossCheckResult,
  });

  if (process.env.NODE_ENV === 'development') {
    console.error('Missing translation:');
    // oxlint-disable-next-line no-console
    console.table({ key, ns, lngs, options, fallbackValue, updateMissing, crossCheckResult });
  }
};

export const missingKeyHandler: MissingKeyHandler = (...args: MissingKeyArguments) => {
  const [lngs, ns, key, fallbackValue, updateMissing, options] = args;
  const report = (crossCheckResult: CrossCheckResult) => {
    reportMissingKey({ lngs, ns, key, fallbackValue, updateMissing, options, crossCheckResult });
  };

  const language = lngs[0];
  if (!language || !ns || !key) {
    report('skipped');
    return;
  }

  void loadAppLocaleResource(language, ns)
    .catch(() => null)
    .then((resource) => {
      if (resource && Object.hasOwn(resource, key)) {
        return;
      }

      report(resource ? 'confirmed_missing' : 'cross_check_unavailable');
    });
};
